import React from 'react';
import { ProductRewardSearchResult, AnalyticsTrackingEvent, isProductEligibleForCheckout } from '@brandclub/common-ui';
import { AddToBagButton } from './AddToBagButtonBase';
import { getPriorityEligibleProduct } from './inpageAddToBagHelpers';
import { MoreRetailersInfo } from './MoreRetailersInfo';
import { useCartUpdates } from '@/api/cache/cartQueryOptions';
import { useTrackActions } from '@/hooks/useTracking';
import { useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import BagDrawer from '../BagButton/BagDrawer';
import { useBCRouting } from '@/providers/BCRoutingContextProvider';
import { useTheme } from '@mui/material';
import { useSplitTreatments } from '@splitsoftware/splitio-react';

export interface AddToBagForSkuProps {
  product: ProductRewardSearchResult;
  buyButtonOnly?: boolean;
}

const AddToBagForSku: React.FC<AddToBagForSkuProps> = ({ product, buyButtonOnly }) => {
  const { data: domainConfig } = useDomainConfig();
  const { mutate: updateCart } = useCartUpdates();
  const [trackAction] = useTrackActions();
  const bcRouting = useBCRouting();
  const theme = useTheme();

  // Check Split treatment for domain-level checkout disable
  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: window.location.hostname,
      bcSplit: (window as any).bcSplit,
    },
  });
  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  const hideBottomLine = (theme.content?.plpAddToBagButton?.signedOutDesktop as any)?.hideBottomLine;

  const priorityProduct = getPriorityEligibleProduct(product, domainConfig?.checkoutAllowedOfferType);

  const isEligibleForCheckout =
    !!priorityProduct &&
    isProductEligibleForCheckout(priorityProduct as ProductRewardSearchResult, domainConfig?.checkoutAllowedOfferType);

  // Used by MoreRetailersInfo/BuyBoxDialog - handles both add-to-cart and view-retailer
  const handleBuyClick = (currentProduct: ProductRewardSearchResult) => {
    if (isEligibleForCheckout && !disableCheckoutByDomain) {
      updateCart({
        action: 'add',
        items: [{ ...priorityProduct, quantity: 1 }],
        retailerId: currentProduct.retailerId,
      });
      trackAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
        stacklineSku: currentProduct.stacklineSku,
        retailerId: currentProduct.retailerId,
        retailerSku: currentProduct.retailerSku,
        retailPrice: currentProduct.retailPrice,
        quantity: 1,
        source: 'brandclub_dtc_inline_button',
      });
      BagDrawer?.show();
    } else {
      trackAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
        stacklineSku: currentProduct.stacklineSku,
        retailerId: currentProduct.retailerId,
        retailerSku: currentProduct.retailerSku,
        retailPrice: currentProduct.retailPrice,
        source: 'brandclub_dtc_inline_button',
      });
      bcRouting.navigate(currentProduct.retailerProductPageUrl, '_blank');
    }
  };

  return (
    <>
      <AddToBagButton
        buyButtonOnly={buyButtonOnly}
        currentBrandclubProduct={priorityProduct}
        originalProduct={product}
        onAddToCart={() => {
          if (priorityProduct) {
            updateCart({
              action: 'add',
              items: [{ ...priorityProduct, quantity: 1 }],
              retailerId: priorityProduct.retailerId,
            });
            trackAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
              stacklineSku: priorityProduct.stacklineSku,
              retailerId: priorityProduct.retailerId,
              retailerSku: priorityProduct.retailerSku,
              retailPrice: priorityProduct.retailPrice,
              quantity: 1,
              source: 'brandclub_dtc_inline_button',
            });
            BagDrawer?.show();
          }
        }}
        onViewRetailer={() => {
          if (priorityProduct) {
            trackAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
              stacklineSku: priorityProduct.stacklineSku,
              retailerId: priorityProduct.retailerId,
              retailerSku: priorityProduct.retailerSku,
              retailPrice: priorityProduct.retailPrice,
              source: 'brandclub_dtc_inline_button',
            });
            bcRouting.navigate(priorityProduct.retailerProductPageUrl, '_blank');
          }
        }}
        isEligibleForCheckout={isEligibleForCheckout}
      />
      {hideBottomLine ? null : <MoreRetailersInfo product={product} handleBuyBoxBuyClick={handleBuyClick} />}
    </>
  );
};

export default AddToBagForSku;
